import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  ActionBar,
  Button,
  Field,
  FormCard,
  PasswordField,
  SectionHeader,
  SelectField,
  StatusBadge,
  ToggleField,
} from "@fawkes/ui";

// jve-settings-ai / Δ4: the React port of the Jmix "AI koppeling" tile. Two records live here:
//   1. the single, org-wide AI-routing record (default provider + optional fallback), GET+PUT
//      /api/ai-settings — a singleton resource, no id.
//   2. the per-provider credentials (API key + model + enabled), GET /api/ai-providers and
//      PUT /api/ai-providers/{provider} — the api now owns this write side too (previously Jmix-only),
//      coexisting with the Jmix card on the same rows/format.
//
// SECRETS ARE WRITE-ONLY. The API never returns a stored key — only a masked status
// ({ keyConfigured, keyLast4 }). Each provider's PasswordField starts empty with a masked placeholder;
// leaving it blank on save keeps the stored key, typing a new value replaces it. So the raw key never
// reaches the client and a save round-trips without re-entering it.

type AiProvider = "CHATGPT" | "CLAUDE" | "GEMINI";

interface AiSettings {
  defaultProvider: AiProvider;
  fallbackEnabled: boolean;
  fallbackProvider: AiProvider | null;
}

interface AiProviderStatus {
  provider: AiProvider;
  enabled: boolean;
  modelName: string | null;
  baseUrl: string | null;
  keyConfigured: boolean;
  keyLast4: string | null;
}

const PROVIDERS: AiProvider[] = ["CHATGPT", "CLAUDE", "GEMINI"];

// Display labels — parity with the Jmix aiProviderLabel() (ChatGPT/Claude/Gemini; same in nl + en).
function providerLabel(provider: AiProvider): string {
  switch (provider) {
    case "CHATGPT":
      return "ChatGPT";
    case "CLAUDE":
      return "Claude";
    case "GEMINI":
      return "Gemini";
  }
}

// The masked "is a key configured" chip — reuses the generic integraties status vocabulary.
function SecretStatusChip({ status }: { status: AiProviderStatus }) {
  const t = useTranslate();
  const text = status.keyConfigured
    ? status.keyLast4
      ? t("instellingen.integraties.status.configuredTail", { args: [status.keyLast4] })
      : t("instellingen.integraties.status.configured")
    : t("instellingen.integraties.status.notConfigured");
  return <StatusBadge tone={status.keyConfigured ? "success" : "neutral"}>{text}</StatusBadge>;
}

// The masked placeholder for the key PasswordField: a dotted tail when set, else "not configured".
/** Een model zoals de aanbieder het noemt. */
interface AiModel {
  id: string;
  naam: string;
}

/** De melding van de server; die zegt wat de aanbieder zelf terugmeldde. */
function foutTekst(fout: unknown): string {
  const bericht = (fout as { message?: string } | undefined)?.message;
  return typeof bericht === "string" ? bericht : "";
}

function secretPlaceholder(status: AiProviderStatus, t: ReturnType<typeof useTranslate>): string {
  if (!status.keyConfigured) return t("instellingen.integraties.secret.empty");
  return status.keyLast4 ? `•••• ${status.keyLast4}` : "••••••••";
}

// One provider's credentials card (key + model + enabled). Owns its own PUT to
// /api/ai-providers/{provider}. Re-seeds from `status` on remount (the parent keys it on the fetched
// status), so after a save the masked placeholder reflects the new last-4 and the key field is blank.
function ProviderKeyCard({ status, onSaved }: { status: AiProviderStatus; onSaved: () => void }) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [enabled, setEnabled] = useState(status.enabled);
  const [modelName, setModelName] = useState(status.modelName ?? "");
  const [apiKey, setApiKey] = useState("");
  const [saved, setSaved] = useState(false);

  // De modellen komen van de aanbieder zelf, niet uit een lijst in onze code: die veroudert sneller
  // dan onze releases. Pas ophalen als er een sleutel is -- zonder sleutel weet de aanbieder niet
  // wie het vraagt, en een rode melding op een nog niet ingevuld scherm helpt niemand.
  const {
    data: modellenData,
    isLoading: modellenLaden,
    isError: modellenFout,
    error: modellenFoutmelding,
  } = useCustom<AiModel[]>({
    url: `ai-providers/${status.provider}/modellen`,
    method: "get",
    queryOptions: { enabled: status.keyConfigured, retry: false },
  });
  // Niet aannemen dat er een lijst terugkomt: een tussenliggende proxy of een oudere server
  // kan iets anders geven, en dan hoort de pagina het te overleven in plaats van zwart te worden.
  const modellen: AiModel[] = Array.isArray(modellenData?.data) ? modellenData.data : [];

  // Staat het opgeslagen model niet in de lijst, dan hoort het er toch bij te staan -- anders
  // verdwijnt het stilletjes uit beeld en denk je dat er niets is ingesteld. Juist zo"n naam die
  // nergens op slaat wil je kunnen zien.
  const onbekendModel = modelName !== "" && !modellen.some((m) => m.id === modelName);

  // Terugvallen op een tekstveld zodra de lijst niet te krijgen is: met een onbereikbare aanbieder
  // moet je nog steeds iets kunnen invullen.
  const modelAlsTekst = modellenFout || (!status.keyConfigured && modellen.length === 0);

  const modelOpties = [
    { value: "", label: t("instellingen.ai.modellen.kies") },
    // Een opgeslagen model dat niet (meer) wordt aangeboden hoort zichtbaar te blijven -- anders
    // lijkt het alsof er niets is ingesteld, terwijl juist dat de oorzaak van de fout is.
    ...(onbekendModel
      ? [{ value: modelName, label: t("instellingen.ai.modellen.onbekend", { args: [modelName] }) }]
      : []),
    ...modellen.map((m) => ({
      value: m.id,
      label: m.naam === m.id ? m.id : `${m.naam} (${m.id})`,
    })),
  ];

  function save() {
    mutate(
      {
        // Relative path: the dataProvider prepends "/api"; an absolute "/api/…" would double to
        // "/api/api/…" (404). A blank apiKey keeps the stored key (write-only-secret contract).
        url: `ai-providers/${status.provider}`,
        method: "put",
        values: { enabled, modelName, apiKey },
      },
      {
        onSuccess: () => {
          setSaved(true);
          setApiKey("");
          onSaved();
        },
      },
    );
  }

  return (
    <div className="settings-integrations-card">
      <div className="settings-integrations-card-header">
        <span className="settings-integrations-card-title">{providerLabel(status.provider)}</span>
        <SecretStatusChip status={status} />
      </div>
      <PasswordField
        label={t("instellingen.ai.field.apiKey")}
        value={apiKey}
        placeholder={secretPlaceholder(status, t)}
        onChange={(e) => {
          setApiKey(e.target.value);
          setSaved(false);
        }}
      />
      <span className="fawkes-muted">{t("instellingen.ai.field.apiKey.keep")}</span>
      {modelAlsTekst ? (
        // Lukt ophalen niet, dan blijft het een invulveld: beter zelf typen dan vastlopen omdat de
        // aanbieder even niet bereikbaar is.
        <>
          <Field
            label={t("instellingen.ai.field.model")}
            value={modelName}
            onChange={(e) => {
              setModelName(e.target.value);
              setSaved(false);
            }}
          />
          <span className="fawkes-muted">
            {status.keyConfigured
              ? foutTekst(modellenFoutmelding) || t("instellingen.ai.modellen.mislukt")
              : t("instellingen.ai.modellen.eerstSleutel")}
          </span>
        </>
      ) : (
        <>
          <SelectField
            label={t("instellingen.ai.field.model")}
            value={modelName}
            disabled={modellenLaden}
            onChange={(e) => {
              setModelName(e.target.value);
              setSaved(false);
            }}
            options={modelOpties}
          />
          {onbekendModel && !modellenLaden && (
            <span className="fawkes-error">
              {t("instellingen.ai.modellen.onbekendUitleg", { args: [modelName] })}
            </span>
          )}
        </>
      )}
      <ToggleField
        label={t("instellingen.ai.field.enabled")}
        checked={enabled}
        onChange={(e) => {
          setEnabled(e.target.checked);
          setSaved(false);
        }}
      />
      {saved && (
        <p className="fawkes-success" role="status">
          {t("instellingen.ai.providerSaved")}
        </p>
      )}
      <div className="settings-integrations-card-actions">
        <Button variant="primary" onClick={save} disabled={isLoading}>
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}

export function AiKoppelingDetail({ onBack }: { onBack: () => void }) {
  const t = useTranslate();
  // Relative resource path: the dataProvider prepends the API base ("/api"); an absolute "/api/…"
  // here would double to "/api/api/…" (404).
  const { data, isLoading, isError, refetch } = useCustom<AiSettings>({ url: "ai-settings", method: "get" });
  const {
    data: providersData,
    isLoading: providersLoading,
    refetch: refetchProviders,
  } = useCustom<AiProviderStatus[]>({ url: "ai-providers", method: "get" });
  const { mutate: save, isLoading: isSaving } = useCustomMutation();

  const [defaultProvider, setDefaultProvider] = useState<AiProvider>("CHATGPT");
  const [fallbackEnabled, setFallbackEnabled] = useState(false);
  const [fallbackProvider, setFallbackProvider] = useState<AiProvider>("CLAUDE");
  const [hydrated, setHydrated] = useState(false);
  const [saved, setSaved] = useState(false);

  // Seed the form from the fetched record once (then it is user-owned until the next save/refetch).
  useEffect(() => {
    if (data?.data && !hydrated) {
      setDefaultProvider(data.data.defaultProvider ?? "CHATGPT");
      setFallbackEnabled(Boolean(data.data.fallbackEnabled));
      setFallbackProvider(data.data.fallbackProvider ?? "CLAUDE");
      setHydrated(true);
    }
  }, [data, hydrated]);

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError) {
    return <p className="fawkes-error">{t("instellingen.ai.loadFailed")}</p>;
  }

  const dirty = () => setSaved(false);

  function onSave() {
    save(
      {
        url: "ai-settings",
        method: "put",
        values: {
          defaultProvider,
          fallbackEnabled,
          // No fallback when the toggle is off — keep the persisted state coherent.
          fallbackProvider: fallbackEnabled ? fallbackProvider : null,
        },
      },
      {
        onSuccess: () => {
          setSaved(true);
          refetch();
        },
      },
    );
  }

  const providerOptions = PROVIDERS.map((p) => ({ value: p, label: providerLabel(p) }));
  // Defensive: the routing screen must still render if /api/ai-providers is absent (no DB / gated off)
  // or returns an unexpected shape — fall back to no provider cards rather than crash.
  const providerStatuses = Array.isArray(providersData?.data) ? providersData.data : [];

  return (
    <>
      <FormCard>
        <SectionHeader>{t("instellingen.ai.title")}</SectionHeader>
        <p className="fawkes-muted settings-ai-detail__breadcrumb">
          {`${t("instellingen.title")} › ${t("instellingen.context.organization")} › ${t(
            "instellingen.tile.integrations.title",
          )} › ${t("instellingen.item.aiKoppeling")}`}
        </p>
        <p className="fawkes-muted">{t("instellingen.ai.description")}</p>

        <div className="fawkes-form-grid">
          <SelectField
            label={t("instellingen.ai.defaultProvider")}
            value={defaultProvider}
            onChange={(e) => {
              setDefaultProvider(e.target.value as AiProvider);
              dirty();
            }}
            options={providerOptions}
          />
          <ToggleField
            label={t("instellingen.ai.fallbackEnabled")}
            checked={fallbackEnabled}
            onChange={(e) => {
              setFallbackEnabled(e.target.checked);
              dirty();
            }}
          />
          <SelectField
            label={t("instellingen.ai.fallbackProvider")}
            value={fallbackProvider}
            disabled={!fallbackEnabled}
            onChange={(e) => {
              setFallbackProvider(e.target.value as AiProvider);
              dirty();
            }}
            options={providerOptions}
          />
        </div>

        {saved && (
          <p className="fawkes-success" role="status">
            {t("instellingen.ai.saved")}
          </p>
        )}

        <ActionBar>
          <Button variant="secondary" onClick={onBack}>
            {t("common.back")}
          </Button>
          <Button onClick={onSave} disabled={isSaving}>
            {t("common.save")}
          </Button>
        </ActionBar>
      </FormCard>

      <FormCard>
        <SectionHeader>{t("instellingen.ai.providersTitle")}</SectionHeader>
        <p className="fawkes-muted">{t("instellingen.ai.providersDescription")}</p>
        {providersLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : (
          <div className="settings-integrations-grid">
            {providerStatuses.map((status) => (
              <ProviderKeyCard
                // Re-seed on the fetched status: a new last-4 after save changes the key, remounting the
                // card with a blank key field and the refreshed masked placeholder.
                key={`${status.provider}-${status.keyConfigured}-${status.keyLast4 ?? ""}`}
                status={status}
                onSaved={() => refetchProviders()}
              />
            ))}
          </div>
        )}
      </FormCard>
    </>
  );
}
